import { TaskPriority } from "@/schema"
import InputLabel from "@ui/badge-input/input-label"
import { BadgeInputSelect } from "@ui/badge-input/select"
import { MaskedIcon } from "@ui/masked-icon"
import { cn } from "@ui/utils"
import { Flag } from "lucide-react"

type PriorityValue = "low" | "medium" | "high" | "urgent"

const PRIORITY_LABELS: Record<PriorityValue, string> = {
  urgent: "Urgent",
  high: "High",
  medium: "Medium",
  low: "Low",
}

const getPriorityIcon = (priority: PriorityValue | null) => {
  switch (priority) {
    case "low":
      return <MaskedIcon src="/icons/low-priority.svg" className="size-3.5" />
    case "medium":
      return <MaskedIcon src="/icons/medium-priority.svg" className="size-3.5" />
    case "high":
      return <MaskedIcon src="/icons/high-priority.svg" className="size-3.5" />
    case "urgent":
      return (
        <div className="bg-icon flex size-3.5 items-center justify-center rounded-sm text-xs font-medium text-white">
          !
        </div>
      )
    default:
      return <Flag size={16} strokeWidth={1.5} />
  }
}

type TaskCreatePrioritySelectorProps = {
  priority: TaskPriority | null
  onPriorityChange: (priority: TaskPriority | null) => void
  className?: string
}

const PRIORITY_OPTIONS = (Object.keys(PRIORITY_LABELS) as PriorityValue[]).map((priority) => ({
  value: priority,
  label: (
    <div className="flex items-center gap-2">
      <div className="text-icon">{getPriorityIcon(priority)}</div>
      <span>{PRIORITY_LABELS[priority]}</span>
    </div>
  ),
}))

const NO_PRIORITY_OPTION = {
  value: "none",
  label: "No priority",
}

export default function TaskCreatePrioritySelector({
  priority,
  onPriorityChange,
  className,
}: TaskCreatePrioritySelectorProps) {
  return (
    <BadgeInputSelect
      id="task-priority-selector"
      value={priority ?? "none"}
      onValueChange={(value) => {
        if (value === "none") {
          onPriorityChange(null)
          return
        }
        onPriorityChange(value as TaskPriority)
      }}
      options={[NO_PRIORITY_OPTION, ...PRIORITY_OPTIONS]}
      className={cn("w-24", className)}
    >
      <InputLabel
        label={
          priority ? (
            <div className="flex items-center justify-end gap-2">
              <div className="text-icon">{getPriorityIcon(priority)}</div>
              <span>{PRIORITY_LABELS[priority]}</span>
            </div>
          ) : (
            "-"
          )
        }
        description="Priority"
        className={cn(priority ? "text-foreground" : "text-muted-foreground")}
      />
    </BadgeInputSelect>
  )
}
